import React from "react";
import Joi from "joi-browser";
import { toast } from "react-toastify";

import Media from "../../common/media";

import FormHeader from "../../common/form/formHeader";
import FormFooter from "../../common/form/formFooter";

import {
  getEntities,
  getEntity,
  saveEntity,
} from "../../../services/apiService";
import { uploadFiles, deleteFile } from "../../../services/fileService";

class ViewpointForm extends Media {
  constructor(props) {
    super(props);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleFileSelect = this.handleFileSelect.bind(this);
    this.handleMapClick = this.handleMapClick.bind(this);

    this.state = {
      data: {
        poi_id: "",
        filename: "",
        image_aspect_ratio: "",

        viewpoint_lat: "",
        viewpoint_long: "",
        viewpoint_image_height: "",
        viewpoint_image_offset: "",
        viewpoint_image_vertical_offset: "",
        transcription_de: "",
        transcription_en: "",

        is_displayed_on_screen: false,
        medianame: "",

        has_viewpoint_angle: false,
        viewpoint_distance: "",
        viewpoint_degree: "",
        viewpoint_alpha: "",
        viewpoint_beta: "",
        viewpoint_gamma: "",
      },
      pois: [],
      selectedPoi: undefined,
      capturedImage: undefined,
      images: [],
      labels: [],
      progresses: [],
      previewImages: [],
      selectedImages: [],
      currentFiles: { filename: "", medianame: "" },
      selectedFiles: { filename: "", medianame: "" },
      inputLabels: { filename: "", medianame: "" },
      types: { filename: "", medianame: "" },
      errors: {},
    };
  }

  // Define the Form Schema
  schema = {
    _id: Joi.string(),
    poi_id: Joi.string().required().label("Histocache"),
    filename: Joi.string().required().label("Image"),
    image_aspect_ratio: Joi.number()
      .min(0.1)
      .max(4)
      .required()
      .label("Aspect Ratio"),

    viewpoint_lat: Joi.number()
      .min(-90)
      .max(90)
      .required()
      .label("Latitude"),
    viewpoint_long: Joi.number()
      .min(-180)
      .max(180)
      .required()
      .label("Longitude"),
    viewpoint_image_height: Joi.number()
      .allow(0)
      .min(1)
      .required()
      .label("Image Height"),
    viewpoint_image_offset: Joi.number()
      .allow(0)
      .min(1)
      .required()
      .label("Image Offset"),
    viewpoint_image_vertical_offset: Joi.number()
      .allow(0)
      .required()
      .label("Vertical Offset"),
    transcription_de: Joi.string()
      .label("Transkription | Deutsch")
      .max(10000)
      .allow(null)
      .allow("")
      .empty(""),
    transcription_en: Joi.string()
      .label("Transcription | English")
      .max(10000)
      .allow(null)
      .allow("")
      .empty(""),

    is_displayed_on_screen: Joi.boolean().label("Has Teaser").required(),
    medianame: Joi.string()
      .label("Teaser File")
      .when("is_displayed_on_screen", {
        is: true,
        then: Joi.required(),
        otherwise: Joi.allow(null).allow("").empty(""),
      }),

    has_viewpoint_angle: Joi.boolean().label("Has Orientation").required(),
    viewpoint_distance: Joi.number()
      .allow(0)
      .min(0)
      .label("Distance")
      .when("has_viewpoint_angle", {
        is: true,
        then: Joi.required(),
        otherwise: Joi.allow(null).allow("").empty(""),
      }),
    viewpoint_degree: Joi.number()
      .allow(0)
      .min(0)
      .max(360)
      .label("Distance")
      .when("has_viewpoint_angle", {
        is: true,
        then: Joi.required(),
        otherwise: Joi.allow(null).allow("").empty(""),
      }),
    viewpoint_alpha: Joi.number()
      .allow(0)
      .min(-270)
      .max(270)
      .label("Alpha")
      .when("has_viewpoint_angle", {
        is: true,
        then: Joi.required(),
        otherwise: Joi.allow(null).allow("").empty(""),
      }),
    viewpoint_beta: Joi.number()
      .allow(0)
      .min(-270)
      .max(270)
      .label("Beta")
      .when("has_viewpoint_angle", {
        is: true,
        then: Joi.required(),
        otherwise: Joi.allow(null).allow("").empty(""),
      }),
    viewpoint_gamma: Joi.number()
      .allow(0)
      .min(-270)
      .max(270)
      .label("Gamma")
      .when("has_viewpoint_angle", {
        is: true,
        then: Joi.required(),
        otherwise: Joi.allow(null).allow("").empty(""),
      }),
  };

  // Retrive the Data from the Server
  async componentDidMount() {
    // Get Pois and Modify
    const { data: allPois } = await getEntities("poi");
    if (allPois.length === 0) return;

    // Get Pois that don't have Poi
    const pois_without_viewpoint = allPois.filter(
      (p) => p.has_viewpoint_location === false
    );

    // Get the Given ID
    const viewpoint_id = this.props.match.params.id;

    // When at least a Poi exists in the database:
    let pois = [];
    if (viewpoint_id === "new") {
      pois = pois_without_viewpoint.map(({ title_en: name, ...rest }) => ({
        name,
        ...rest,
      }));
    } else {
      pois = allPois.map(({ title_en: name, ...rest }) => ({
        name,
        ...rest,
      }));
    }

    this.setState({ pois });

    if (viewpoint_id === "new") return;

    // Get Item related to the Given ID
    const { data: viewpoint } = await getEntity("viewpoint", viewpoint_id);
    if (!viewpoint) return this.props.history.replace("/not-found");
    // Find the Poi itself
    const poi = pois.find((p) => p._id === viewpoint.poi_id);

    this.setState({
      data: this.mapToViewModel(viewpoint),
      selectedPoi: { lat: poi.lat, long: poi.long },
      currentFiles: {
        filename: viewpoint.filename,
        medianame: viewpoint.medianame,
      },
    });
  }

  // Update the priority after selcting the Poi
  async componentDidUpdate(prevProps, prevState) {
    if (prevProps.match.params.id === "new") {
      if (prevState.data.poi_id !== this.state.data.poi_id) {
        const { data, pois } = this.state;

        // Get coressponding Poi
        const poi = pois.find((p) => p._id === data.poi_id);
        if (!poi) {
          this.setState({ selectedPoi: undefined });
          return;
        }

        // Define an initial Viewpoint
        const initial_data = { ...this.state.data };
        initial_data["viewpoint_lat"] = poi.lat - 0.00005;
        initial_data["viewpoint_long"] = poi.long - 0.0001;

        this.setState({ selectedPoi: poi, data: initial_data });
      }
    }
  }

  // Map current Viewpoint to the Form Inputs
  mapToViewModel(viewpoint) {
    return {
      _id: viewpoint._id,
      poi_id: viewpoint.poi_id,
      filename: viewpoint.filename,
      image_aspect_ratio: viewpoint.image_aspect_ratio,

      viewpoint_lat: viewpoint.viewpoint_lat,
      viewpoint_long: viewpoint.viewpoint_long,
      viewpoint_image_height: viewpoint.viewpoint_image_height,
      viewpoint_image_offset: viewpoint.viewpoint_image_offset,
      viewpoint_image_vertical_offset:
        viewpoint.viewpoint_image_vertical_offset,

      transcription_de: viewpoint.transcription_de,
      transcription_en: viewpoint.transcription_en,

      is_displayed_on_screen: viewpoint.is_displayed_on_screen,
      medianame: viewpoint.medianame,

      has_viewpoint_angle: viewpoint.has_viewpoint_angle,
      viewpoint_distance: viewpoint.viewpoint_distance,
      viewpoint_degree: viewpoint.viewpoint_degree,
      viewpoint_alpha: viewpoint.viewpoint_alpha,
      viewpoint_beta: viewpoint.viewpoint_beta,
      viewpoint_gamma: viewpoint.viewpoint_gamma,
    };
  }

  // Form Submit Function
  doSubmit = async () => {
    // Data for saving to the Database
    const { data, selectedImages, pois, currentFiles } = this.state;

    try {
      // Saving Viewpoint
      const response = await saveEntity(
        "viewpoint",
        this.cleanDataObject(data)
      );
      if (response.data && response.status !== 200) return;

      // Uploading Files
      const header = `pois/${data.poi_id}`;

      for (let i = 0; i < selectedImages.length; i++) {
        const progressInfos = [...this.state.progresses];

        await uploadFiles(selectedImages[i], header, (event) => {
          progressInfos[i].percentage = Math.round(
            (100 * event.loaded) / event.total
          );

          this.setState({ progresses: progressInfos });
        })
          .then(() => {
            // Success Message of Files uploading
            toast.success(
              `File: ${progressInfos[i].fileName} \n uploaded sucessfully!`
            );
          })
          .catch(() => {
            progressInfos[i].percentage = 0;

            this.setState({ progresses: progressInfos });

            // Error Message of Files uploading
            toast.error(
              `File: ${progressInfos[i].fileName} \n can not be uploaded.`
            );
          });
      }

      // Success Message of Viewpoint Saving
      const poi = pois.find((p) => p._id === response.data.poi_id);

      toast.success(
        <div>
          Viewpoint of Histocache: "{poi.name}"
          <br /> saved successfully.
        </div>
      );

      // Delete Previous Files if exists.
      if (currentFiles.filename && data.filename !== currentFiles.filename)
        await deleteFile("pois", currentFiles.filename);

      if (currentFiles.medianame && data.medianame !== currentFiles.medianame)
        await deleteFile("pois", currentFiles.medianame);
    } catch (ex) {
      toast.error(ex.response?.message ?? ex.response.data?.message);

      return;
    }

    // Return to Viewpoints TableView after saving
    this.handleBack();
  };

  // Data Object cleaning Function
  cleanDataObject(obj) {
    for (var property in obj) {
      if (
        obj[property] === null ||
        obj[property] === undefined ||
        obj[property] === ""
      ) {
        delete obj[property];
      }
    }
    return obj;
  }

  // Back button Event Handler
  handleBack = () => {
    // Table Pagination Query
    const searchParams = new URLSearchParams(window.location.search);
    const currentPageQuery = searchParams.get("currentPage");
    const pageSizeQuery = searchParams.get("pageSize");

    // Return to Viewpoints TableView after saving
    if (currentPageQuery && pageSizeQuery) {
      this.props.history.push(
        `/dashboard/viewpoints?pageSize=${pageSizeQuery}&currentPage=${currentPageQuery}`
      );
    } else {
      this.props.history.push("/dashboard/viewpoints");
    }
  };

  render() {
    const id = this.props.match.params.id;
    const { pois, selectedPoi, data, progresses, errors } = this.state;

    return (
      <div className="col alt-table-responsive">
        <FormHeader name="Viewpoint" icon="bx bxs-location-plus" id={id} />
        <form
          onSubmit={this.handleSubmit}
          className="form-group"
          encType="multipart/form-data"
        >
          <div className="row pt-2 mt-2">
            <div className=" col-sm-6">
              <div className="row align-items-center">
                <div className="col-sm-8">
                  {this.renderSelect("poi_id", "Histocache", pois)}
                </div>
                <div className="col-sm-4">
                  {data.has_viewpoint_angle && (
                    <div className="row mt-3">
                      <div className="col-sm-12">
                        {this.renderModal(true, "filename")}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="row mb-4 pt-2">
                <div className="col">
                  {this.renderToggle(
                    "has_viewpoint_angle",
                    "Has Orientation",
                    data.poi_id === "" ? true : false
                  )}
                </div>
              </div>
              <div className="row mb-4 mt-2">
                <div className="col">
                  {this.renderToggle("is_displayed_on_screen", "Has Teaser")}
                </div>
              </div>

              {data.has_viewpoint_angle && <hr />}

              {data.has_viewpoint_angle && (
                <div className="row mt-4">
                  <div className="col-sm-4">
                    {this.renderInput("viewpoint_alpha", "Alpha", "number")}
                  </div>
                  <div className="col-sm-4">
                    {this.renderInput("viewpoint_beta", "Beta", "number")}
                  </div>
                  <div className="col-sm-4">
                    {this.renderInput("viewpoint_gamma", "Gamma", "number")}
                  </div>
                </div>
              )}

              <hr />

              <div className="row mt-4">
                <div className="col-sm-4">
                  {this.renderInput(
                    "viewpoint_image_height",
                    "Image Height",
                    "number",
                    "",
                    false,
                    1
                  )}
                </div>
                <div className="col-sm-4">
                  {this.renderInput(
                    "viewpoint_image_offset",
                    "Image Offset",
                    "number"
                  )}
                </div>
                <div className="col-sm-4">
                  {this.renderInput(
                    "viewpoint_image_vertical_offset",
                    "Vertical Offset",
                    "number"
                  )}
                </div>
              </div>

              <hr />

              <div className="row mt-4">
                <div className="col">
                  {this.renderTextBox(
                    "transcription_en",
                    "Transcription  | English"
                  )}
                </div>
              </div>
              <div className="row mt-2">
                <div className="col">
                  {this.renderTextBox(
                    "transcription_de",
                    "Transkription | Deutsch"
                  )}
                </div>
              </div>
            </div>

            <div className=" col-sm-6 border-left">
              <div className="row mt-1">
                <div className="col">
                  {this.renderMap(
                    "Viewpoint Location",
                    "viewpoint_lat",
                    "viewpoint_long",
                    selectedPoi
                  )}
                </div>
              </div>
              <div className="row pt-4">
                <div className="col-sm-6">
                  {this.renderInput("viewpoint_lat", "Latitude", "number")}
                </div>
                <div className="col-sm-6">
                  {this.renderInput("viewpoint_long", "Longitude", "number")}
                </div>
              </div>
              <div className="row">
                <div className="col-sm-6">
                  {this.renderInput("viewpoint_distance", "Distance", "number")}
                </div>
                <div className="col-sm-6">
                  {this.renderInput("viewpoint_degree", "Degree", "number")}
                </div>
              </div>

              <hr />

              <div className="row">
                <div className="col">
                  {this.renderMediaUploader(
                    "Image",
                    "filename",
                    "pois",
                    "poi_id",
                    "image/*",
                    "image_aspect_ratio"
                  )}
                </div>
              </div>

              {data.is_displayed_on_screen && <hr className="mt-3 pb-2" />}

              <div className="row">
                <div className="col">
                  {data.is_displayed_on_screen &&
                    this.renderMediaUploader(
                      "Teaser",
                      "medianame",
                      "pois",
                      "poi_id",
                      "video/*,audio/*"
                    )}
                </div>
              </div>

              <div className="row">
                <div className="col">{this.renderErrors(errors)}</div>
              </div>
              <div className="row">
                <div className="col">{this.renderUploadStatus(progresses)}</div>
              </div>
            </div>
          </div>
          <FormFooter
            onBackClick={this.handleBack}
            onValidate={this.validate()}
          />
        </form>
      </div>
    );
  }
}

export default ViewpointForm;
